import React, { useCallback, useState } from 'react';
import { FormControl, TextField } from '@material-ui/core';

import { HouseNumberControlProps } from './HouseNumberControl.interfaces';
import { ControlContainer } from 'app/components/ControlContainer';
import { UtilService } from 'app/services/util.service';
import { DoneAdornment } from 'app/components/DoneAdornment';
import { isValidHouseNumber } from 'app/validators/validateHouseNumber';
import { isAutofillChange } from 'app/shared/helpers/isAutofillChange';

function hasWhiteSpace(value: string): boolean {
  return /\s/g.test(value);
}

const HouseNumberControl = ({
  input,
  input: { value: inputValue },
  meta,
  meta: { touched, valid },
  subLabel,
  tooltip,
  disabled,
  helperText,
  label,
  placeholder,
  width,
  isQuestionValid,
  onControlBlur,
  onClick,
  onValueChange,
}: HouseNumberControlProps) => {
  const [focused, setFocused] = useState(false);

  const handleBlur = useCallback(
    (autofilledValue?: string) => {
      const isAutofilled = autofilledValue !== undefined;
      const currentValue =
        autofilledValue === undefined ? inputValue : autofilledValue;
      const shouldPatch = isValidHouseNumber(currentValue) || !currentValue;
      const trimmedValue = currentValue.trim();
      if (trimmedValue !== currentValue) {
        input.onChange(trimmedValue);
      }

      setFocused(false);

      input.onBlur(currentValue);

      if (onControlBlur) {
        onControlBlur(
          shouldPatch,
          true,
          false,
          isAutofilled,
          isAutofilled ? trimmedValue : undefined
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inputValue, input.onChange, input.onBlur, onControlBlur]
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const {
        target: { value },
      } = event;

      const isAutofilled = isAutofillChange(event.target);
      const nextValue = isAutofilled ? value.replace(/\s/g, '') : value;

      if (isAutofilled && !isValidHouseNumber(nextValue)) {
        return;
      }

      if (hasWhiteSpace(nextValue)) {
        return;
      }

      input.onChange(nextValue);

      if (onValueChange) {
        onValueChange(nextValue, false, false);
      }

      if (isAutofilled) {
        handleBlur(nextValue);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [input.onChange, onValueChange, handleBlur]
  );

  const handleClick = useCallback(() => {
    if (onClick) {
      onClick();
    }
  }, [onClick]);

  return (
    <ControlContainer
      inputId={input.name}
      tooltip={tooltip}
      meta={meta}
      subLabel={subLabel}
      helperText={helperText}
      floating
      floated={focused || UtilService.hasValue(inputValue)}
      focused={focused}
      width={width}
    >
      <FormControl fullWidth>
        <TextField
          {...input}
          id={input.name}
          inputProps={{
            maxLength: 11,
          }}
          type="text"
          onChange={handleChange}
          onBlur={() => handleBlur()}
          onClick={handleClick}
          onFocus={() => setFocused(true)}
          label={label}
          placeholder={placeholder}
          InputLabelProps={{
            shrink: UtilService.hasValue(inputValue) || focused,
          }}
          InputProps={{
            startAdornment: (isQuestionValid ||
              isQuestionValid === undefined) &&
              !focused &&
              isValidHouseNumber(inputValue) &&
              UtilService.hasValue(inputValue) && <DoneAdornment />,
          }}
          error={touched && !valid}
          disabled={disabled}
          variant={'outlined'}
          margin={'dense'}
        />
      </FormControl>
    </ControlContainer>
  );
};

export default HouseNumberControl;
